import type { SVGProps } from 'react';

/** Small inline icon set (24px grid, stroke 1.8). Decorative by default; pass `title` to make one meaningful. */
export type IconName =
  | 'home' | 'briefcase' | 'compass' | 'book' | 'clipboard' | 'chart' | 'folder' | 'sparkles' | 'bell' | 'user' | 'menu' | 'close' | 'check' | 'lock' | 'unlock' | 'arrow-right' | 'arrow-left' | 'search' | 'calendar' | 'shield' | 'terminal' | 'chat' | 'star' | 'trophy' | 'map' | 'info' | 'warning' | 'error' | 'play' | 'flag' | 'users' | 'ticket' | 'mail' | 'settings' | 'sun' | 'moon' | 'clock' | 'target' | 'lightbulb' | 'code' | 'graph' | 'list' | 'flask' | 'server' | 'compass-star';

const PATHS: Record<IconName, string> = {
  home: 'M3 11 12 3l9 8M5 10v10h5v-6h4v6h5V10',
  briefcase: 'M4 8h16v11H4zM9 8V5h6v3M4 13h16',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm3.5-12.5-2 5-5 2 2-5z',
  book: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zm0 0v16M8 7h7',
  clipboard: 'M9 4h6v3H9zM7 6H5v15h14V6h-2M9 12h6M9 16h4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  folder: 'M3 6h6l2 2h10v11H3z',
  sparkles: 'M12 3v4M12 17v4M3 12h4M17 12h4M6.5 6.5l2.5 2.5M15 15l2.5 2.5M6.5 17.5 9 15M15 9l2.5-2.5',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 9a8 8 0 0 1 16 0',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'm5 12 4 4L19 6',
  lock: 'M6 11h12v10H6zM9 11V7a3 3 0 0 1 6 0v4',
  unlock: 'M6 11h12v10H6zM9 11V7a3 3 0 0 1 5.8-1',
  'arrow-right': 'M4 12h16m-6-6 6 6-6 6',
  'arrow-left': 'M20 12H4m6-6-6 6 6 6',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zm9 2-4-4',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  shield: 'M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z',
  terminal: 'M4 5h16v14H4zM7 9l3 3-3 3M12 15h5',
  chat: 'M4 5h16v11H9l-5 4z',
  star: 'm12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
  trophy: 'M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 21h8M9 17h6',
  map: 'M3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-10v5m0-8v.5',
  warning: 'M12 4 3 20h18zM12 10v4m0 3v.5',
  error: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 9l6 6m0-6-6 6',
  play: 'M7 4v16l13-8z',
  flag: 'M5 21V4h11l-1 4 1 4H5',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zm-6 9a6 6 0 0 1 12 0M16 4a3.5 3.5 0 0 1 0 7m1 3a6 6 0 0 1 4 6',
  ticket: 'M4 8a2 2 0 0 0 0 4v0a2 2 0 0 0 0 4h16a2 2 0 0 0 0-4v0a2 2 0 0 0 0-4zM14 8v8',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7-3 2 1-1 3-2.3-.3-1.5 1.5.3 2.3-3 1-1-2h-2l-1 2-3-1 .3-2.3-1.5-1.5L3 16l-1-3 2-1v-2L2 9l1-3 2.3.3 1.5-1.5L6.5 2.5l3-1 1 2h2l1-2 3 1-.3 2.3 1.5 1.5L21 6l1 3-2 1z',
  sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-13v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4',
  moon: 'M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-13v5l3 2',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-4a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0-4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  lightbulb: 'M9 18h6m-5 3h4M12 3a6 6 0 0 0-3.5 10.9c.9.7 1.5 1.6 1.5 2.6h4c0-1 .6-1.9 1.5-2.6A6 6 0 0 0 12 3z',
  code: 'm8 8-5 4 5 4m8-8 5 4-5 4M14 4l-4 16',
  graph: 'M6 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM6 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM7.8 6l8.4 5M16.2 13 7.8 18M6 7v10',
  list: 'M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01',
  flask: 'M9 3h6M10 3v6L4.5 18.5A1.7 1.7 0 0 0 6 21h12a1.7 1.7 0 0 0 1.5-2.5L14 9V3M7.5 14h9',
  server: 'M4 4h16v6H4zM4 14h16v6H4zM7.5 7h.01M7.5 17h.01M11 7h5M11 17h5',
  'compass-star': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-13.5 1.3 2.8 3 .4-2.2 2.1.6 3-2.7-1.5-2.7 1.5.6-3-2.2-2.1 3-.4z',
};

export function Icon({ name, title, size = 20, ...rest }: { name: IconName; title?: string; size?: number } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} focusable="false" {...rest}>
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  );
}
